'use client';

import React, { forwardRef, useMemo, useEffect, useState } from 'react';
import { FEATURE_FLAG_KEYS } from 'shop-ui/constants';
import { type BasicShopUIPropTypes, type BuyerLayoutProps } from 'shop-ui/types';
import { useBuyerProductStore } from 'app/store/product';
import { SSR } from './ssr';
import { Carousel } from './Carousel';
import { productImageGalleryTypeKey } from './define';
import { Wall } from './Wall';

export interface ProductImageGalleryProps
    extends BasicShopUIPropTypes,
        BuyerLayoutProps<Product> {
    // 自行加入其他 props
    content: string;
    isEditingMode: boolean;
}

export type SlideOptions = {
    slideOptions: {
        medias: (ProductDisplayMedia & { specName?: string })[];
        alt: string;
        type: string;
        thumbDirection: string;
    };
};

export const ProductImageGallery = forwardRef<
    HTMLDivElement & HTMLUListElement,
    ProductImageGalleryProps
>((props, ref) => {
    const { styleMap, __ssr = {}, isBuyer } = props;

    const [hydrated, setHydrated] = useState(false);

    const productStore = useBuyerProductStore();

    const slideOptions = useMemo(() => {
        const productMedias = productStore.product?.medias || [];

        const productSpecMedias =
            productStore.product?.spec_metas
                .map((i) =>
                    i.specs.map((j) => ({
                        ...j.image,
                        id: j.id,
                        specName: j.name,
                    }))
                )
                .flat()
                .filter((i) => i.src) || [];

        const medias = [...productMedias, ...productSpecMedias];

        return {
            medias,
            alt: productStore.product?.name || '',
            type: styleMap
                ? styleMap[FEATURE_FLAG_KEYS.PRODUCT_IMAGE_GALLERY_TYPE]
                : 'standard',
            thumbDirection: styleMap
                ? styleMap[FEATURE_FLAG_KEYS.PRODUCT_IMAGE_GALLERY_DIRECTION]
                : 'horizontal',
        };
    }, [styleMap, productStore.product]);

    useEffect(() => {
        setHydrated(true);
    }, []);

    if (!hydrated && isBuyer) {
        return <SSR.Carousel __ssr={__ssr} />;
    }

    switch (slideOptions.type) {
        case productImageGalleryTypeKey.standard:
        case productImageGalleryTypeKey.feature:
        case productImageGalleryTypeKey.scroll:
            return <Carousel ref={ref} {...props} slideOptions={slideOptions} />;

        case productImageGalleryTypeKey.wall:
        case productImageGalleryTypeKey.wall2:
            return <Wall ref={ref} {...props} slideOptions={slideOptions} />;

        default:
            return null;
    }
});

ProductImageGallery.displayName = 'ProductImageGallery';
